// src/auth/AuthProvider.jsx
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  onAuthStateChanged,
  signOut as fbSignOut,
  signInWithEmailAndPassword as fbSignInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth } from "../../../firebase";
import SessionExpiredModal from "../../../components/SessionExpiredModal";
import { remoteLog } from "../../../utils/remoteLog";
import { startPhotoSyncListeners } from "../../../utils/photoSync";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [role, setRole]           = useState(null); // 'tecnico' | 'supervisor' | 'admin' | 'cliente'
  const [ready, setReady]         = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigate = useNavigate();
  const isRefreshingRef   = useRef(false);
  const handledExpiryRef  = useRef(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      handledExpiryRef.current = false;
      if (u) {
        // Se o usuário re-autenticou via modal, fecha o modal
        setSessionExpired(false);
        remoteLog('info', 'auth_state_changed', 'Usuário autenticado', { uid: u.uid, email: u.email });
        // Inicia listeners de sync de fotos pendentes (idempotente — só registra uma vez)
        startPhotoSyncListeners();
        try {
          const db   = getFirestore();
          const snap = await getDoc(doc(db, "users", u.uid));
          setRole(snap.exists() ? (snap.data()?.role ?? null) : null);
        } catch {
          setRole(null);
        }
      } else {
        setRole(null);
        remoteLog('info', 'auth_state_changed', 'Usuário desconectado', {});
      }
      setReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    // Decodifica o campo `exp` do JWT localmente — sem nenhuma chamada de rede.
    // Os tokens Firebase são JWTs padrão (header.payload.sig em base64url).
    const getTokenExpiryMs = (jwtString) => {
      try {
        const part = jwtString.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(part));
        return payload.exp ? payload.exp * 1000 : 0;
      } catch {
        return 0;
      }
    };

    // Aguarda evento 'online' do browser ou timeout.
    // ATENÇÃO: navigator.onLine é pouco confiável no Android 10 — por isso
    // esta função é apenas um fallback; a lógica principal evita chamadas
    // de rede desnecessárias verificando a expiração local primeiro.
    const aguardarRede = (timeoutMs = 120_000) =>
      new Promise((resolve) => {
        if (navigator.onLine) { resolve(true); return; }
        const timer = setTimeout(() => { cleanup(); resolve(false); }, timeoutMs);
        const onOnline = () => { cleanup(); resolve(true); };
        const cleanup = () => {
          window.removeEventListener('online', onOnline);
          clearTimeout(timer);
        };
        window.addEventListener('online', onOnline);
      });

    const revalidateSession = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser || isRefreshingRef.current) return;

      isRefreshingRef.current = true;

      try {
        // ── Passo 1: obtém o token do CACHE (sem rede, instantâneo) ──────────
        const cachedToken = await currentUser.getIdToken(false);
        const msUntilExpiry = getTokenExpiryMs(cachedToken) - Date.now();

        // ── Passo 2: token ainda válido por mais de 5 minutos ────────────────
        // Não faz NENHUMA chamada de rede. Resolve o problema do Android 10
        // onde visibilitychange dispara antes da rede estar pronta.
        if (msUntilExpiry > 5 * 60 * 1000) {
          isRefreshingRef.current = false;
          return;
        }

        // ── Passo 3: token expira em breve (< 5 min) → força renovação ───────
        remoteLog('info', 'session_token_expiring',
          `Token expira em ${Math.round(msUntilExpiry / 1000)}s, renovando…`, {});
        await currentUser.getIdToken(true);
        handledExpiryRef.current = false;
        isRefreshingRef.current = false;
        return;

      } catch (err) {
        // Erro que não é de rede (conta desativada, token revogado) → modal imediato
        if (err?.code !== 'auth/network-request-failed') {
          isRefreshingRef.current = false;
          if (handledExpiryRef.current) return;
          handledExpiryRef.current = true;
          console.error("AuthProvider: erro de auth (não-rede)", err);
          remoteLog('error', 'session_refresh_failed',
            `Falha de auth (não-rede): ${err?.code || err?.message || 'desconhecido'}`,
            { code: err?.code, message: err?.message }
          );
          setSessionExpired(true);
          return;
        }

        // Erro de rede durante a renovação → aguarda reconexão
        remoteLog('warn', 'session_network_down',
          'Rede indisponível durante renovação de token, aguardando…',
          { code: err?.code }
        );
      }

      // Aguarda evento 'online' ou timeout de 2 minutos
      const redeVoltou = await aguardarRede(120_000);

      if (redeVoltou) {
        // Delay de 2s para rede estabilizar (Android 10 anuncia 'online' cedo demais)
        await new Promise(r => setTimeout(r, 2000));
        try {
          await currentUser.getIdToken(true);
          handledExpiryRef.current = false;
          isRefreshingRef.current = false;
          remoteLog('info', 'session_network_recovered', 'Token renovado após reconexão', {});
          return;
        } catch (err2) {
          console.error("AuthProvider: falha após reconexão de rede", err2);
          remoteLog('error', 'session_refresh_failed',
            `Falha após reconexão: ${err2?.code || err2?.message || 'desconhecido'}`,
            { code: err2?.code, message: err2?.message }
          );
        }
      } else {
        remoteLog('error', 'session_refresh_failed',
          'Sem rede por mais de 2 minutos, exibindo modal',
          { code: 'auth/network-request-failed', timeoutMs: 120000 }
        );
      }

      isRefreshingRef.current = false;
      if (handledExpiryRef.current) return;
      handledExpiryRef.current = true;
      setSessionExpired(true);
    };

    const handleFocus = () => {
      void revalidateSession();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void revalidateSession();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void revalidateSession();
      }
    }, 5 * 60 * 1000);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [navigate]);

  const getIdToken = async (forceRefresh = false) => {
    const u = auth.currentUser;
    if (!u) return null;
    return u.getIdToken(forceRefresh);
  };

  // Chamado pelo botão "Sair e descartar dados" do modal
  const handleFinalSignOut = async () => {
    remoteLog('info', 'manual_signout', 'Usuário saiu via modal de sessão expirada', {});
    setSessionExpired(false);
    try { await fbSignOut(auth); } catch {}
    navigate('/auth/sign-in/default', { replace: true });
  };

  const signInWithEmailAndPassword = (email, password) =>
    fbSignInWithEmailAndPassword(auth, email, password);
  const signInWithGoogle = () =>
    signInWithPopup(auth, new GoogleAuthProvider());
  const signOut = () => fbSignOut(auth);

  const value = useMemo(
    () => ({ user, role, ready, getIdToken, signInWithEmailAndPassword, signInWithGoogle, signOut }),
    [user, role, ready]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExpiredModal
        isOpen={sessionExpired}
        userEmail={user?.email || ''}
        onSuccess={() => {
          remoteLog('info', 'session_expired_modal_reauth_ok', 'Re-autenticação via modal bem-sucedida', {});
          handledExpiryRef.current = false;
          setSessionExpired(false);
        }}
        onSignOut={handleFinalSignOut}
      />
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
